// intentAnalysisService removed - using LLM-based analysis
import { groundedResponseService } from './groundedResponseService';
import { ResponseGenerationRequest } from '../types/groundedResponse';
import { contentGenerationService } from './contentGenerationService';
import { intelligentChatService } from './intelligentChatService';

// Importar funcionalidades da PoC integradas
import type { ConversationMemoryManager, ConversationMessage } from './langchainMemory';

export interface LangChainIntegrationConfig {
  enableLangChain: boolean;
  enableFallback: boolean;
  maxRetries: number;
  timeoutMs: number;
}

export interface IntegratedIntentAnalysis {
  analysis: any;
  confidence: number;
  source: 'langchain' | 'backend';
  processingTime: number;
  metadata?: any;
}

export interface IntegratedGroundedResponse {
  answer: string;
  sources: any[];
  metadata: any;
  source: 'langchain' | 'backend';
  processingTime: number;
}

export class LangChainIntegrationService {
  private config: LangChainIntegrationConfig;
  private isLangChainAvailable: boolean = false;
  private orchestrator: any | null = null;

  constructor(config: Partial<LangChainIntegrationConfig> = {}) {
    this.config = {
      enableLangChain: true,
      enableFallback: true,
      maxRetries: 3,
      timeoutMs: 30000,
      ...config
    };
    
    this.initializeLangChain();
  }

  /**
   * Inicializa o LangChain e verifica disponibilidade
   */
  private async initializeLangChain() {
    try {
      // Recuperar API key diretamente do env como todo o sistema
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
      

      if (apiKey && apiKey.length > 10) {
        try {
          // Import dinâmico para evitar problemas de dependência circular
          const orchestratorModule = await import(/* webpackChunkName: "langchain-orchestrator" */ './langchainOrchestrator');
          const LangChainOrchestrator = orchestratorModule.LangChainOrchestrator;
          
          this.orchestrator = new LangChainOrchestrator(apiKey);
          this.isLangChainAvailable = true;
        } catch (importError) {
          this.isLangChainAvailable = false;
        }
      } else {
        this.isLangChainAvailable = false;
      }
    } catch (error) {
      this.isLangChainAvailable = false;
    }
  }

  /**
   * Análise de intenção integrada (LangChain + Fallback)
   */
  async analyzeIntentIntegrated(
    query: string, 
    conversationHistory: any[] = [], 
    recentDocuments: string[] = [],
    threadId?: string
  ): Promise<IntegratedIntentAnalysis> {
    const startTime = Date.now();

    // Tentar LangChain Orchestrator primeiro se disponível

    if (this.config.enableLangChain && this.isLangChainAvailable && this.orchestrator) {
      try {
        
        if (conversationHistory && conversationHistory.length > 0) {
          // 🧠 CORREÇÃO CRÍTICA: Limpar memória antes de carregar para evitar duplicações
          await this.orchestrator.memory.clearConversation();
          
          for (const message of conversationHistory) {
            if (message.role === 'user') {
              await this.orchestrator.memory.addUserMessage(message.content);
            } else if (message.role === 'assistant') {
              await this.orchestrator.memory.addAssistantMessage(message.content);
            }
          }
        }
        
        const result = await this.orchestrator.processUserInput(query, { skipMemorySave: true });

        return {
          analysis: {
            intent: result.intent,
            entities: [],
            needsVectorSearch: result.sources.length > 0,
            confidence: result.metadata.confidence,
            reasoning: result.processingSteps.join('; '),
            answer: result.answer, // ✅ Adicionar a resposta completa
            sources: result.sources
          },
          confidence: result.metadata.confidence,
          source: 'langchain',
          processingTime: result.metadata.processingTime,
          metadata: {
            fallbackUsed: false,
            processingStrategy: 'orchestrator_primary',
            searchType: result.metadata.searchType
          }
        };
      } catch (error) {
        // LangChain falhou, prosseguir para fallback
      }
    }

    // Se chegou aqui, LangChain não está disponível
    throw new Error('LangChain não está disponível e não há fallback configurado');
  }

  /**
   * Geração de resposta com grounding integrada
   */
  async generateGroundedResponseIntegrated(
    query: string,
    searchResults: any[],
    intent: any,
    conversationHistory: any[] = [],
    threadId?: string
  ): Promise<IntegratedGroundedResponse> {
    const startTime = Date.now();

    // Tentar LangChain Orchestrator primeiro se disponível
    if (this.config.enableLangChain && this.isLangChainAvailable && this.orchestrator) {
      try {
        const result = await this.orchestrator.processUserInput(query);

        return {
          answer: result.answer,
          sources: result.sources,
          metadata: result.metadata,
          source: 'langchain',
          processingTime: result.metadata.processingTime
        };
      } catch (error) {
        // LangChain falhou, prosseguir para fallback
      }
    }

    // Fallback removido - usar apenas LangChain
    throw new Error('Geração de resposta não disponível - LangChain não está funcionando');

    throw new Error('Nenhuma opção de processamento disponível');
  }

  /**
   * Busca inteligente integrada - COMPATÍVEL COM THREADS
   */
  async searchIntelligentlyIntegrated(
    query: string,
    threadId?: string,
    options: any = {}
  ): Promise<any> {
    // Tentar usar orquestrador primeiro (que usa nova API robusta)
    if (this.config.enableLangChain && this.isLangChainAvailable && this.orchestrator) {
      try {
        const result = await this.orchestrator.processUserInput(query, options);
        
        // Retornar no formato esperado pelas APIs de thread
        return {
          message: result.answer,
          documentReferences: result.sources,
          intent: result.intent,
          processingSteps: result.processingSteps,
          metadata: result.metadata,
          threadId: threadId,
          timestamp: new Date().toISOString()
        };
      } catch (error) {
        // Orquestrador falhou, usar backend tradicional
      }
    }

    // Fallback removido - usar apenas LangChain
    throw new Error('Busca inteligente não disponível - LangChain não está funcionando');
  }

  /**
   * Detecção de ambiguidade integrada
   */
  async detectAmbiguityIntegrated(
    query: string,
    threadId?: string
  ): Promise<any> {
    // Detecção de ambiguidade agora é feita pela análise de intenção da LangChain
    try {
      const intentAnalysis = await this.analyzeIntentIntegrated(query, [], [], threadId);
      return {
        isAmbiguous: intentAnalysis.analysis?.isVague || false,
        suggestions: intentAnalysis.analysis?.suggestedRefinements || [],
        confidence: intentAnalysis.confidence
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Geração de conteúdo integrada
   */
  async generateContentIntegrated(
    prompt: string,
    documentType?: string,
    context?: string,
    maxLength?: number,
    style?: 'formal' | 'informal' | 'technical' | 'creative'
  ): Promise<any> {
    try {
      const request = {
        prompt,
        documentType,
        context,
        maxLength,
        style
      };

      return await contentGenerationService.generateContent(request);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Processa entrada do usuário usando LangChain Orchestrator
   */
  async processUserInputIntegrated(
    query: string,
    conversationHistory: any[] = [],
    threadId?: string
  ): Promise<any> {
    try {
      // Tentar LangChain Orchestrator primeiro se disponível
      if (this.config.enableLangChain && this.isLangChainAvailable && this.orchestrator) {
        try {
          // Carregar histórico na memória do orchestrator
          await this.orchestrator.memory.clearConversation();
          
          // Se é primeira mensagem (histórico vazio), adicionar contexto para evitar new_conversation
          if (conversationHistory.length === 0) {
            // Adicionar uma mensagem de contexto para que o LangChain não detecte como new_conversation
            await this.orchestrator.memory.addAssistantMessage("Olá! Como posso te ajudar com documentos?");
          }
          
          for (const message of conversationHistory) {
            if (message.role === 'user') {
              await this.orchestrator.memory.addUserMessage(message.content);
            } else if (message.role === 'assistant') {
              await this.orchestrator.memory.addAssistantMessage(message.content);
            }
          }

          const result = await this.orchestrator.processUserInput(query, { skipMemorySave: true });

          return {
            answer: result.answer,
            sources: result.sources || [],
            metadata: {
              ...result.metadata,
              source: 'langchain',
              processingStrategy: 'orchestrator_primary',
              threadId: threadId
            },
            source: 'langchain',
            processingTime: result.metadata?.processingTime || Date.now()
          };
        } catch (error) {
          // LangChain falhou, prosseguir para fallback
        }
      }

      // Fallback removido - usar apenas LangChain
      throw new Error('Processamento de mensagem não disponível - LangChain não está funcionando');
    } catch (error) {
      throw error;
    }
  }

  /**
   * Utilitários privados
   */
  private formatConversationHistory(history: any[]): any[] {
    return history.slice(-10).map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp || new Date()
    }));
  }

  private formatSearchResults(results: any[]): any[] {
    return results.map(result => ({
      documentId: result.documentId || result.id,
      documentTitle: result.documentTitle || result.title,
      content: result.content || result.text,
      score: result.score || result.relevanceScore,
      metadata: result.metadata || {},
      jurisdiction: result.jurisdiction || 'N/A',
      documentType: result.documentType || 'documento'
    }));
  }

  private extractUserContext(history: any[]): any {
    if (history.length === 0) {
      return {
        expertise: 'BEGINNER',
        preferredDocumentTypes: [],
        frequentJurisdictions: [],
        searchBehavior: 'BROAD'
      };
    }

    // Analisar histórico para extrair contexto do usuário
    const userMessages = history.filter(msg => msg.role === 'user');
    const technicalTerms = userMessages.filter(msg => 
      this.containsTechnicalTerms(msg.content)
    ).length;

    let expertise: 'BEGINNER' | 'INTERMEDIATE' | 'EXPERT' = 'BEGINNER';
    if (technicalTerms > userMessages.length * 0.7) expertise = 'EXPERT';
    else if (technicalTerms > userMessages.length * 0.3) expertise = 'INTERMEDIATE';

    return {
      expertise,
      preferredDocumentTypes: this.extractDocumentTypes(history),
      frequentJurisdictions: this.extractJurisdictions(history),
      searchBehavior: this.analyzeSearchBehavior(history)
    };
  }

  private containsTechnicalTerms(content: string): boolean {
    const technicalTerms = [
      'jurisdição', 'competência', 'atribuições', 'procedimento', 'processo',
      'norma', 'regulamento', 'legislação', 'decreto', 'portaria'
    ];
    return technicalTerms.some(term => content.toLowerCase().includes(term));
  }

  private extractDocumentTypes(history: any[]): string[] {
    const types = new Set<string>();
    history.forEach(msg => {
      if (msg.content) {
        const content = msg.content.toLowerCase();
        if (content.includes('contrato')) types.add('contrato');
        if (content.includes('norma')) types.add('norma');
        if (content.includes('procedimento')) types.add('procedimento');
        if (content.includes('relatório')) types.add('relatório');
      }
    });
    return Array.from(types);
  }

  private extractJurisdictions(history: any[]): string[] {
    const jurisdictions = new Set<string>();
    history.forEach(msg => {
      if (msg.content) {
        const content = msg.content.toLowerCase();
        if (content.includes('são paulo')) jurisdictions.add('São Paulo');
        if (content.includes('rio de janeiro')) jurisdictions.add('Rio de Janeiro');
        if (content.includes('minas gerais')) jurisdictions.add('Minas Gerais');
        if (content.includes('federal')) jurisdictions.add('Federal');
      }
    });
    return Array.from(jurisdictions);
  }

  private analyzeSearchBehavior(history: any[]): 'BROAD' | 'SPECIFIC' | 'EXPLORATORY' {
    if (history.length < 3) return 'BROAD';
    
    const recentQueries = history.slice(-3).filter(msg => msg.role === 'user');
    const specificQueries = recentQueries.filter(msg => 
      msg.content.includes('onde') || msg.content.includes('quando') || msg.content.includes('quem')
    ).length;

    if (specificQueries > recentQueries.length * 0.6) return 'SPECIFIC';
    if (recentQueries.some(msg => msg.content.includes('explique') || msg.content.includes('como'))) return 'EXPLORATORY';
    return 'BROAD';
  }


  /**
   * Timeout wrapper para operações LangChain
   */
  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]);
  }

  /**
   * Status do serviço
   */
  getStatus() {
    return {
      langChainAvailable: this.isLangChainAvailable,
      orchestratorAvailable: !!this.orchestrator,
      config: this.config,
      services: {
        intentAnalysis: this.isLangChainAvailable,
        groundedResponse: this.isLangChainAvailable,
        contentGeneration: true,
        orchestrator: !!this.orchestrator
      }
    };
  }

  /**
   * Atualizar configuração
   */
  updateConfig(newConfig: Partial<LangChainIntegrationConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Métodos do orquestrador
   */
  async streamResponse(
    query: string,
    options?: { forceSearchType?: 'text' | 'vector' | 'hybrid' }
  ): Promise<AsyncGenerator<{
    type: 'step' | 'content' | 'sources' | 'complete' | 'intent';
    data: any;
  }, void, unknown> | null> {
    if (this.orchestrator && this.isLangChainAvailable) {
      return this.orchestrator.streamResponse(query, options);
    }
    return null;
  }

  async clearConversation(): Promise<void> {
    if (this.orchestrator) {
      await this.orchestrator.clearConversation();
    }
  }

  // Adicionar mensagem do usuário à memória (para carregar histórico)
  async addUserMessageToMemory(content: string): Promise<void> {
    if (this.orchestrator) {
      await this.orchestrator.memory.addUserMessage(content);
    }
  }

  // Adicionar mensagem do assistente à memória (para carregar histórico)
  async addAssistantMessageToMemory(content: string): Promise<void> {
    if (this.orchestrator) {
      await this.orchestrator.memory.addAssistantMessage(content);
    }
  }

  getConversationHistory() {
    if (this.orchestrator) {
      return this.orchestrator.getConversationHistory();
    }
    return [];
  }

  getConversationStats() {
    if (this.orchestrator) {
      return this.orchestrator.getStats();
    }
    return null;
  }
}

// Instância singleton
export const langchainIntegrationService = new LangChainIntegrationService();
